import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Plus, Users, ChevronDown, ChevronRight, Loader, X, UserPlus, Trash2, Shield , Mail } from 'lucide-react';

export default function AdminTenants() {
  const [tenants, setTenants]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expanded, setExpanded]       = useState({});
  const [tenantUsers, setTenantUsers] = useState({});
  const [showNew, setShowNew]         = useState(false);
  const [showNewUser, setShowNewUser] = useState(null); // tenant_id
  const [creating, setCreating]       = useState(false);
  const [form, setForm] = useState({ name: '', admin_email: '', admin_password: '', admin_full_name: '' });
  const [userForm, setUserForm] = useState({ email: '', password: '', full_name: '', is_admin: false });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await adminApi.listTenants();
      setTenants(data);
    } catch { toast.error('Failed to load tenants'); }
    finally { setLoading(false); }
  };

  const loadUsers = async (tenantId) => {
    try {
      const { data } = await adminApi.listTenantUsers(tenantId);
      setTenantUsers(u => ({ ...u, [tenantId]: data }));
    } catch { toast.error('Failed to load users'); }
  };

  const toggleExpand = async (id) => {
    const next = { ...expanded, [id]: !expanded[id] };
    setExpanded(next);
    if (next[id] && !tenantUsers[id]) await loadUsers(id);
  };

  const handleToggleActive = async (id) => {
    try {
      await adminApi.toggleTenant(id);
      setTenants(ts => ts.map(t => t.id === id ? { ...t, active: !t.active } : t));
    } catch { toast.error('Failed to update tenant'); }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await adminApi.createTenant(form);
      toast.success(`Tenant "${form.name}" created`);
      setShowNew(false);
      setForm({ name: '', admin_email: '', admin_password: '', admin_full_name: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create tenant'); }
    finally { setCreating(false); }
  };

  const handleCreateUser = async (tenantId) => {
    setCreating(true);
    try {
      await adminApi.createTenantUser(tenantId, userForm);
      toast.success('User created');
      setShowNewUser(null);
      setUserForm({ email: '', password: '', full_name: '', is_admin: false });
      await loadUsers(tenantId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create user'); }
    finally { setCreating(false); }
  };

  const handleDeleteUser = async (tenantId, userId, email) => {
    if (!window.confirm(`Remove user ${email}?`)) return;
    try {
      await adminApi.deleteTenantUser(tenantId, userId);
      toast.success('User removed');
      await loadUsers(tenantId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove user'); }
  };

  const handleSendInvite = async (tenantId, userId, email) => {
    try {
      await adminApi.sendInvite(tenantId, userId);
      toast.success(`Invite sent to ${email}`);
      // Refresh user list to update invite status
      await loadUsers(tenantId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send invite'); }
  };

  const handleDeleteTenant = async (tenant) => {
    const confirm1 = window.confirm(
      `Delete tenant "${tenant.name}"?\n\nThis will permanently delete:\n• All users for this tenant\n• All job history and logs\n• SFTP configuration and AWS resources (S3 bucket, Transfer Family user)\n• All translated files\n\nThis cannot be undone.`
    );
    if (!confirm1) return;
    const confirm2 = window.prompt(`Type the tenant name to confirm deletion:\n"${tenant.name}"`);
    if (confirm2 !== tenant.name) {
      toast.error('Tenant name did not match — deletion cancelled');
      return;
    }
    try {
      const { data } = await adminApi.deleteTenant(tenant.id);
      toast.success(`Tenant "${tenant.name}" deleted`);
      if (data.warnings?.length) {
        toast.error(`Deleted with warnings: ${data.warnings.join(', ')}`, { duration: 8000 });
      }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete tenant'); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="fade-in">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Tenants</h1>
          <p className="page-subtitle">Manage tenants and their users. Each tenant has its own SFTP connection and job history.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          <Plus size={15} /> New Tenant
        </button>
      </div>

      {/* New tenant modal */}
      {showNew && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 440, maxWidth: '90vw' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>New Tenant</h2>
              <button onClick={() => setShowNew(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[['Tenant name', 'name', 'text', 'Acme Corp'], ['Admin email', 'admin_email', 'email', 'admin@acme.com'],
                ['Admin password', 'admin_password', 'password', ''], ['Admin full name', 'admin_full_name', 'text', 'Jane Smith']
              ].map(([label, key, type, ph]) => (
                <div key={key}>
                  <label className="input-label">{label}</label>
                  <input type={type} placeholder={ph} className="input" value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                </div>
              ))}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button className="btn btn-ghost" onClick={() => setShowNew(false)} style={{ flex: 1 }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleCreate} disabled={creating} style={{ flex: 1 }}>
                  {creating ? <Loader size={15} className="spin" /> : null} Create Tenant
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New user modal */}
      {showNewUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card" style={{ width: 420, maxWidth: '90vw' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Add User</h2>
              <button onClick={() => setShowNewUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[['Email', 'email', 'email', 'user@example.com'], ['Password', 'password', 'password', ''], ['Full name', 'full_name', 'text', 'Jane Smith']].map(([label, key, type, ph]) => (
                <div key={key}>
                  <label className="input-label">{label}</label>
                  <input type={type} placeholder={ph} className="input" value={userForm[key]}
                    onChange={e => setUserForm(f => ({ ...f, [key]: e.target.value }))} />
                </div>
              ))}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={userForm.is_admin} onChange={e => setUserForm(f => ({ ...f, is_admin: e.target.checked }))} />
                <span style={{ fontSize: 14, color: 'var(--text-mid)' }}>Platform admin</span>
              </label>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button className="btn btn-ghost" onClick={() => setShowNewUser(null)} style={{ flex: 1 }}>Cancel</button>
                <button className="btn btn-primary" onClick={() => handleCreateUser(showNewUser)} disabled={creating} style={{ flex: 1 }}>
                  {creating ? <Loader size={15} className="spin" /> : null} Add User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {tenants.map(tenant => (
          <div key={tenant.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {/* Tenant row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => toggleExpand(tenant.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 0 }}>
                  {expanded[tenant.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text)' }}>{tenant.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                    ID: {tenant.id.slice(0, 8)}… · Created {new Date(tenant.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: tenant.active ? 'rgba(34,197,94,0.12)' : 'rgba(107,114,128,0.15)', color: tenant.active ? 'var(--green)' : 'var(--text-dim)', border: `1px solid ${tenant.active ? 'rgba(34,197,94,0.25)' : 'transparent'}` }}>
                  {tenant.active ? 'Active' : 'Inactive'}
                </span>
                <button onClick={() => handleToggleActive(tenant.id)} className="btn btn-ghost btn-sm">
                  {tenant.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDeleteTenant(tenant)}
                  className="btn btn-ghost btn-sm"
                  style={{ color: 'var(--red)', borderColor: 'rgba(220,38,38,0.3)' }}
                  title="Permanently delete tenant and all data"
                >
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>

            {/* Expanded users section */}
            {expanded[tenant.id] && (
              <div style={{ borderTop: '1px solid var(--navy-border)', padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Users size={14} style={{ color: 'var(--text-dim)' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-mid)' }}>Users</span>
                  </div>
                  <button onClick={() => setShowNewUser(tenant.id)} className="btn btn-ghost btn-sm">
                    <UserPlus size={13} /> Add User
                  </button>
                </div>

                {!tenantUsers[tenant.id] ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                    <Loader size={18} className="spin" style={{ color: 'var(--text-dim)' }} />
                  </div>
                ) : tenantUsers[tenant.id].length === 0 ? (
                  <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: 0 }}>No users yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {tenantUsers[tenant.id].map(u => (
                      <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--navy)', borderRadius: 8, border: '1px solid var(--navy-border)' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 13, color: 'var(--text)' }}>{u.email}</span>
                            {u.is_admin && <Shield size={12} style={{ color: 'var(--amber)' }} />}
                            {u.activated_at
                              ? <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(34,197,94,0.1)', color: 'var(--green)', border: '1px solid rgba(34,197,94,0.2)', fontWeight: 600 }}>Active</span>
                              : <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.1)', color: 'var(--amber)', border: '1px solid rgba(245,158,11,0.2)', fontWeight: 600 }}>Pending</span>
                            }
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                            {u.full_name || 'No name'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          {!u.activated_at && (
                            <button
                              onClick={() => handleSendInvite(tenant.id, u.id, u.email)}
                              title={u.invite_pending ? 'Resend invite email' : 'Send invite email'}
                              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(37,99,235,0.35)', background: 'rgba(37,99,235,0.08)', color: 'var(--blue)', cursor: 'pointer' }}>
                              <Mail size={11} />
                              {u.invite_pending ? 'Resend Invite' : 'Send Invite'}
                            </button>
                          )}
                          <button onClick={() => handleDeleteUser(tenant.id, u.id, u.email)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 6 }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {tenants.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: 40 }}>
            <Users size={36} style={{ color: 'var(--text-dim)', margin: '0 auto 12px' }} />
            <p style={{ color: 'var(--text-dim)', margin: 0 }}>No tenants yet. Create one to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}
